import _, { isObject } from "lodash";

import { getGroupAuthType } from "@/utils/authToken";
import { httpAuth } from "@/api/httpAuth";

import { GROUP_AUTH_HEADER_NAME } from "../constants";

export const providerRequests = {
  add_care_plan_objectives: function ({ data, urlParams }) {
    return httpAuth
      .post(`/v1/care-plan/${urlParams.patientId}/add-objectives`, {
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json();
  },

  add_consent: function ({ data, urlParams }) {
    return httpAuth
      .post(`/v1/patient_management/${urlParams.patientId}/add_consent`, {
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json();
  },

  begin_ninetyDays_care_plan: function ({ data, searchParams }) {
    return httpAuth
      .post("/v2/provider/begin_ninety_day", {
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
        searchParams: { patientId: searchParams.patientId },
      })
      .json();
  },

  check_and_fetch_eligibility_record: function ({ data }) {
    return httpAuth
      .post("/v1/insurance_eligibility/authenticated_check_eligibility", { json: data })
      .json();
  },

  create_new_patient: function ({ data }) {
    return httpAuth.post("/v1/provider/register_patient", { json: data }).json();
  },

  // TODO: migrate to providerRequestsTyped.delete_progress_note — remaining callers:
  //   ProgressNotes.js
  delete_progress_note: function ({ data, urlParams }) {
    return httpAuth
      .post(`/v1/patient_management/${urlParams.patientId}/delete_progress_notes`, { json: data })
      .json();
  },

  fetch_90_day_care_widget_data: function ({ searchParams }) {
    return httpAuth
      .post("/v1/provider/ninety_day_count", {
        searchParams: { end_date: searchParams.end_date, start_date: searchParams.start_date },
      })
      .json();
  },

  fetch_aptihealth_tips: function ({ context }) {
    return httpAuth.post("/v1/provider/tips", { context }).json();
  },

  fetch_completed_calls: function ({ urlParams }) {
    return httpAuth
      .post(`/v1/patient_management/${urlParams.patientId}/get_completed_calls`)
      .json();
  },

  fetch_ICD10_dataset: function () {
    return httpAuth.post("/v1/provider/ICD_10_data").json();
  },

  fetch_patient_count: function () {
    return httpAuth.post("/v1/provider/patient_count").json();
  },

  fetch_patient_list: function ({ context, searchParams } = {}) {
    return httpAuth
      .post("/v1/provider/patient_list", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        ...(searchParams?.providerId && { searchParams: { provider_id: searchParams.providerId } }),
      })
      .json();
  },

  fetch_practice_members: function () {
    return httpAuth.post("/v1/provider/practice_members").json();
  },

  /**
   * Fetches providers availability for appointments.
   * @param {Object} - An Object which may contain `data`,`urlParams`, `queryParams` and more.
   *
   * Note: `data` shall be `{ date:'YYYY-MM-DD',provider_username:'yyxxzz' }`
   *        if api is calleupdate_patient_detailsd from anywhere except provider's account,
   *        and `{date:'YYYY-MM-DD'}`, only in case when called via provider's account
   */
  fetch_provider_availability: function ({ data }) {
    return httpAuth.post("/v2/provider/availability", { json: data }).json();
  },

  fetch_provider_banner: function ({ context }) {
    return httpAuth.post("/v1/provider/banner", { context }).json();
  },

  fetch_provider_draft_notes: function ({ context } = {}) {
    return httpAuth
      .post("/v1/provider/get_provider_draft_notes", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json();
  },

  fetch_provider_draft_notes_count: function ({ context } = {}) {
    return httpAuth
      .post("/v1/provider/get_provider_draft_notes_count", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json();
  },

  fetch_provider_profile: function ({ context, searchParams } = {}) {
    return httpAuth
      .post("/v1/provider/profile_get", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        ...(searchParams?.providerId && { searchParams: { provider_id: searchParams.providerId } }),
      })
      .json();
  },

  fetch_provider_profile_on_behalf_of: function ({ context, searchParams } = {}) {
    return httpAuth
      .post("/v1/provider/profile_get", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams: { patient_id: searchParams.patientId, provider_id: searchParams.providerId },
      })
      .json();
  },

  get_care_plan: function ({ data, urlParams }) {
    return httpAuth
      .post(`/v1/care-plan/${urlParams.patientId}/profile`, {
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json();
  },

  get_patient_consents: function ({ data, urlParams }) {
    return httpAuth
      .post(`/v1/patient_management/${urlParams.patientId}/get_latest_consents`, {
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json();
  },

  post_outreach_attempt: function ({ data, urlParams }) {
    return httpAuth
      .post(`/v1/patient_management/${urlParams.patientId}/outreach_attempt`, {
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json();
  },

  post_patient_clinical_assessment: function ({ data, searchParams, urlParams }) {
    return httpAuth
      .post(`/v1/patient_management/${urlParams.patientId}/post_clinical_assessment`, {
        json: data,
        searchParams: { type: searchParams.type },
      })
      .json();
  },

  post_progress_note: function ({ context, data, urlParams }) {
    return httpAuth
      .post(`/v1/patient_management/${urlParams.patientId}/post_progress_notes`, {
        context,
        json: data,
      })
      .json();
  },

  put_patient_clinical_assessment: function ({ data, searchParams, urlParams }) {
    return httpAuth
      .post(`/v1/patient_management/${urlParams.patientId}/put_clinical_assessment`, {
        json: data,
        searchParams: {
          browserTimezone: searchParams.browserTimezone,
          id: searchParams.id,
          isComplete: searchParams.isComplete,
          type: searchParams.type,
        },
      })
      .json();
  },

  put_progress_note: function ({ context, data, urlParams }) {
    return httpAuth
      .post(`/v1/patient_management/${urlParams.patientId}/put_progress_notes`, {
        context,
        json: data,
        // disable timeout for long-polling endpoint — needs more time than the default (10 s)
        timeout: false,
      })
      .json();
  },

  /** API for resending Referral code in Provider patient list section */
  resend_referral_code: function ({ context, searchParams } = {}) {
    return httpAuth
      .post("/v1/provider/resend-referral", {
        context,
        searchParams: { code: searchParams.referralCode },
      })
      .json();
  },

  /**
   * Used for both create and delete with respective `method` type in query params
   */
  set_away_schedule: function ({ data, searchParams }) {
    data["isLocal"] = data["isLocal"] ? data["isLocal"] : false;

    return httpAuth
      .post("/v2/provider/away_slots", {
        json: data,
        searchParams: { method: searchParams.method },
      })
      .json();
  },

  set_provider_comment: function ({ data, urlParams }) {
    return httpAuth
      .post(`/v1/patient_management/${urlParams.patientId}/report-comment`, { json: data })
      .json();
  },

  toggle_patient_read_only_message_thread: function ({ data, urlParams }) {
    return httpAuth
      .post(
        `/v1/patient_management/${urlParams.patientId}/toggle_patient_read_only_message_thread`,
        {
          headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
          json: data,
        },
      )
      .json();
  },

  update_away_schedule: function ({ data }) {
    return httpAuth.post("/v2/provider/away_slots?method=edit", { json: data }).json();
  },

  update_patient_details: function ({ data, urlParams }) {
    if (_.has(data, "assessment_status") && isObject(data["assessment_status"])) {
      delete data["assessment_status"];
    }

    return httpAuth
      .post(`/v1/patient_management/${urlParams.patientId}/update_patient_details`, {
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json();
  },

  update_profile: function ({ data, searchParams }) {
    return httpAuth
      .post("/v2/provider/profile_update", {
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
        ...(searchParams?.providerId && { searchParams: { provider_id: searchParams.providerId } }),
      })
      .json();
  },
};
